import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import prettyBytes from 'pretty-bytes';
import { MenuItem } from '../../../components/Dropdown/MenuItem';
import { TableDropdown } from '../../../components/Dropdown/TableDropdown';
import { deleteAssetRequest, getAssetUsage } from '../actions/assets';
import { hideToast } from '../actions/toast';
import { runWithBusyToast } from '../utils/busyToast';

const AssetMenu = ({ item: asset }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleAssetDelete = async () => {
    const { key, name } = asset;
    // Find which sketches use this asset so we can warn before removing the file from them. This
    // scan reads EVERY one of the user's projects and runs before the confirm dialog appears, so
    // without the busy toast the click looks dead for as long as it takes.
    const affected = await dispatch(
      runWithBusyToast('Toast.CheckingFileUsage', () =>
        dispatch(getAssetUsage(key))
      )
    );
    // window.confirm blocks the main thread; a busy toast frozen behind the dialog reads worse than
    // none, and the scan it described is finished anyway.
    dispatch(hideToast());
    const count = affected ? affected.length : 0;
    const message =
      count > 0
        ? t('AssetList.DeleteConfirmationInUse', {
            name,
            count,
            defaultValue:
              'WARNING! "{{name}}" is used in {{count}} sketch(es). Deleting it will remove the file from them.'
          })
        : t('Common.DeleteConfirmation', { name });
    if (window.confirm(message)) {
      dispatch(deleteAssetRequest(key, affected || undefined));
    }
  };

  return (
    <TableDropdown aria-label={t('AssetList.ToggleOpenCloseARIA')}>
      <MenuItem onClick={handleAssetDelete}>{t('AssetList.Delete')}</MenuItem>
      <MenuItem href={asset.url} target="_blank" rel="noopener noreferrer">
        {t('AssetList.OpenNewTab')}
      </MenuItem>
    </TableDropdown>
  );
};

AssetMenu.propTypes = {
  item: PropTypes.shape({
    key: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired
};

const AssetListRow = ({ asset, username }) => (
  <tr className="asset-table__row" key={asset.key}>
    <th scope="row">
      <a href={asset.url} target="_blank" rel="noopener noreferrer">
        {asset.name}
      </a>
    </th>
    <td className="asset-table__size-column">{prettyBytes(asset.size)}</td>
    <td>
      {asset.sketchId && (
        <Link to={`/${username}/sketches/${asset.sketchId}`}>
          {asset.sketchName}
        </Link>
      )}
    </td>
    <td className="asset-table__dropdown-column">
      <AssetMenu item={asset} />
    </td>
  </tr>
);

AssetListRow.propTypes = {
  asset: PropTypes.shape({
    key: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    sketchId: PropTypes.string,
    sketchName: PropTypes.string,
    size: PropTypes.number.isRequired
  }).isRequired,
  username: PropTypes.string.isRequired
};

export default AssetListRow;
